import { ExpandMore } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    List,
    Stack,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDate, formatTimeRange } from "~/modules/app/util/formatters";
import { WeatherReport } from "~/modules/project/components/WeatherReport";
import { isWeatherReportValid } from "~/modules/project/functions/weather";

import { ObservationListItem } from "./ObservationListItem";
import { UsePlannerVisitWorkerReportShape } from "./usePlannerVisitWorkerReport";

interface FieldworkerFeedbackProps {
    data: UsePlannerVisitWorkerReportShape;
}

export function FieldworkerFeedback({ data }: FieldworkerFeedbackProps) {
    const { t } = useTranslation("fieldworker");

    const startedAt = parseStringToDate(data.startedAt as string);
    const stoppedAt = parseStringToDate(data.stoppedAt as string);

    return (
        <>
            <Card variant="outlined">
                <CardHeader
                    action={
                        <Typography>
                            {formatDate(startedAt)} | {formatTimeRange(startedAt, stoppedAt)}
                        </Typography>
                    }
                    title={data.plannerVisit.plannerArea.name}
                    subheader={data.plannerVisit.protocol.name}
                />
                {data.feedbackComment !== null && (
                    <CardContent>
                        <Alert severity="info">
                            <Typography>{t("FieldworkerFeedbackScreen.feedback")}:</Typography>
                            {data.feedbackComment}
                        </Alert>
                    </CardContent>
                )}
            </Card>

            <Accordion component={Card} elevation={0} sx={{ border: 1, borderRadius: 1, borderColor: "divider" }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">{t("FieldworkerFeedbackScreen.visitConditions")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack columnGap={2} direction="row">
                        {isWeatherReportValid(data.startForm) ? (
                            <WeatherReport data={data.startForm} label={t("FieldworkerFeedbackScreen.titleStartForm")} />
                        ) : (
                            <Box sx={{ flex: 1 / 2 }}>
                                <Divider>{t("FieldworkerFeedbackScreen.titleStartForm")}</Divider>
                                <Typography>{t("FieldworkerFeedbackScreen.visitConditionsUnavailable")}</Typography>
                            </Box>
                        )}
                        {isWeatherReportValid(data.submittedForm) ? (
                            <WeatherReport data={data.submittedForm} label={t("FieldworkerFeedbackScreen.titleSubmittedForm")} />
                        ) : (
                            <Box sx={{ flex: 1 / 2 }}>
                                <Divider>{t("FieldworkerFeedbackScreen.titleSubmittedForm")}</Divider>
                                <Typography>{t("FieldworkerFeedbackScreen.visitConditionsUnavailable")}</Typography>
                            </Box>
                        )}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {Array.isArray(data.observations) && data.observations.length > 0 ? (
                <>
                    <Typography variant="h5">{t("FieldworkerFeedbackScreen.observations")}</Typography>
                    <List sx={{ rowGap: 2, display: "flex", flexDirection: "column" }}>
                        {data.observations?.map(observation => (
                            <ObservationListItem key={observation.id} observation={observation} />
                        ))}
                    </List>
                </>
            ) : (
                <Typography>{t("FieldworkerFeedbackScreen.noObservations")}</Typography>
            )}
        </>
    );
}
